import { type ButtonHTMLAttributes, type ReactNode, type Ref } from 'react';
import clsx from 'clsx';
import './TapChip.css';

export type TapChipAnswerState = 'idle' | 'correct' | 'wrong';

export interface TapChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children: ReactNode;
  /** Render as a ghost placeholder (token moved to answer row). */
  hidden?: boolean;
  /** When the chip is in the answer row, tint it by answer state. */
  answerState?: TapChipAnswerState;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * TapChip — a pill-shaped word token. Two lives:
 *   1. In the "word bank" (pool) — fully visible, tappable.
 *   2. In the "answer row" — with answerState tint post-check.
 * When moved out of the pool, render it with `hidden` to preserve layout.
 */
export function TapChip({
  children,
  hidden,
  answerState = 'idle',
  type,
  className,
  disabled,
  ref,
  ...rest
}: TapChipProps) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={clsx('fluent-tapchip', className)}
      disabled={disabled}
      aria-hidden={hidden || undefined}
      tabIndex={hidden ? -1 : undefined}
      data-hidden={hidden || undefined}
      data-answer={answerState === 'idle' ? undefined : answerState}
      {...rest}
    >
      {children}
    </button>
  );
}
